// src/components/OrderEntry/components/EmailComposeDialog.jsx
// ============================================================================
// BLOCK EMAIL.DIALOG.0 — Email compose dialog (UI only)
// ----------------------------------------------------------------------------
// Intent:
// - Provide an in-app compose form (To / Subject / Message)
// - Parent controls open/close + draft values
// - On Submit: call onSubmit(draft)
// ============================================================================
import React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

export default function EmailComposeDialog({
  open,
  title = 'Compose Email',
  draft,
  onChangeDraft,
  onCancel,
  onSubmit,
}) {
  const safe = React.useMemo(
    () => draft || { to: '', subject: '', message: '', attachment: null },
    [draft]
  );
  const fileInputRef = React.useRef(null);
  const [attachmentError, setAttachmentError] = React.useState('');

  const handleOpenFilePicker = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveAttachment = React.useCallback(() => {
    setAttachmentError('');
    onChangeDraft({ ...safe, attachment: null });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onChangeDraft, safe]);

  const handleFileChange = React.useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
      if (!isPdf) {
        setAttachmentError('Only PDF attachments are allowed.');
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const rawResult = String(reader.result || '');
        const content = rawResult.includes(',') ? rawResult.split(',').pop() : rawResult;

        setAttachmentError('');
        onChangeDraft({
          ...safe,
          attachment: {
            filename: String(file.name || 'attachment.pdf').trim() || 'attachment.pdf',
            contentType: 'application/pdf',
            content,
          },
        });
      };

      reader.onerror = () => {
        setAttachmentError('Unable to read the selected PDF.');
        event.target.value = '';
      };

      reader.readAsDataURL(file);
    },
    [onChangeDraft, safe]
  );

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="To"
            value={safe.to || ''}
            onChange={(e) => onChangeDraft({ ...safe, to: e.target.value })}
            fullWidth
            size="small"
          />

          <TextField
            label="Subject"
            value={safe.subject || ''}
            onChange={(e) => onChangeDraft({ ...safe, subject: e.target.value })}
            fullWidth
            size="small"
          />

          <TextField
            label="Message"
            value={safe.message || ''}
            onChange={(e) => onChangeDraft({ ...safe, message: e.target.value })}
            fullWidth
            multiline
            minRows={8}
          />

          {attachmentError ? (
            <Typography variant="body2" color="error">
              {attachmentError}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          onChange={handleFileChange}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto', minWidth: 0 }}>
          <Button variant="outlined" onClick={handleOpenFilePicker}>
            Add Attachment
          </Button>
          {safe.attachment?.filename ? (
            <>
              <Typography
                variant="body2"
                sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {safe.attachment.filename}
              </Typography>
              <Button size="small" onClick={handleRemoveAttachment}>
                Remove
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              PDF only
            </Typography>
          )}
        </Box>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(safe)}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
